import { useContext } from 'react';
import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { useExportInboundEmailForDebug } from '@/api/communications/useExportInboundEmailForDebug';
import { useGT } from 'gt-react';

interface EmailExportButtonProps {
  /**
   * Inbound email to export. When undefined/null the button hides itself —
   * no useful debug dump can be produced without an id.
   */
  inboundEmailId: string | null | undefined;
  /**
   * Visual size of the button. Defaults to `sm` to fit existing toolbars.
   */
  size?: 'sm' | 'default';
}

/**
 * Admin-only "Export email" button.
 *
 * Calls the `inboundEmails.exportForDebug` RPC and downloads the returned
 * payload as a JSON file the developer can hand to a local backend to
 * reimport and reprocess the email from `splitDocumentsHandler`.
 *
 * Hidden for non-admins, in end-user view, or when no inbound email id
 * is available.
 */
export function EmailExportButton({
  inboundEmailId,
  size = 'sm',
}: EmailExportButtonProps) {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);

  const exportMutation = useExportInboundEmailForDebug();

  if (!userIsAdmin || !inboundEmailId) {
    return null;
  }

  return (
    <Button
      variant="outline"
      size={size}
      disabled={exportMutation.isPending}
      onClick={(event) => {
        event.stopPropagation();
        exportMutation.mutate(inboundEmailId);
      }}
      title={gt(
        'Export raw email + documents as JSON for local debug (admin only)',
        { context: 'email export button tooltip, admin-only debug feature' },
      )}
    >
      <DownloadSimpleIcon className="h-4 w-4" />
      {exportMutation.isPending
        ? gt('Exporting…', {
            context: 'email export button loading state',
          })
        : gt('Export email', { context: 'email export button label' })}
    </Button>
  );
}
